/**
 * VehicleCard - Visar detaljerad information om ett fordon
 * Kombinerar data från vehicles-tabellen och Car.info API
 * Layout: Grunddata överst, tabbar nedtill
 * 
 * Refaktorerad: State och logik flyttat till useVehicleCard hook
 * Types flyttat till types/vehicle.ts
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, ArrowPathIcon, TruckIcon, MagnifyingGlassIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import VehicleAttachments from './VehicleAttachments';
import VehicleEquipmentSection from './VehicleEquipmentSection';
import VehicleServiceTab from './VehicleServiceTab';
import VehicleTiresTab from './VehicleTiresTab';
import VehicleEconomyTab from './VehicleEconomyTab';
import VehicleGeneralInfoTab from './VehicleGeneralInfoTab';
import VehicleBookingInfoTab from './VehicleBookingInfoTab';
import VehicleFuelCardsTab from './VehicleFuelCardsTab';
import VehicleFuelTransactionsTab from './VehicleFuelTransactionsTab';
import VehiclePlacementTab from './VehiclePlacementTab';
import VehicleChargingTab from './VehicleChargingTab';
import VehicleSpecificationsTab from './VehicleSpecificationsTab';
import VehicleDevicePnPTab from './VehicleDevicePnPTab';
import { extractBatteryCapacityKwh } from '@/utils/carInfoBattery';
import VehicleReplacementChainBar from './VehicleReplacementChainBar';
import { useVehicleCard } from '@/hooks/useVehicleCard';
import { useVehicleReferenceNames } from '@/hooks/useVehicleReferenceNames';
import { VehicleCardProps, TabId } from '@/types/vehicle';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import { formatFuelTypeLabel } from '@/utils/fuelTypeHelpers';
import { isCarInfoLeasingActive } from '@/utils/vehicleOwnership';
import { ROUTES } from '@/constants/routes';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/store/authStore';
import { fetchReplacementChain, type ReplacementChainResult } from '@/services/deque/replacementChainApi';
import api from '@/services/api/client';
import { VehiclePositionMapModal, type VehicleMapPosition } from '@/components/maps/VehiclePositionMapModal';

import DateTimeLocalInput from '@/components/common/DateTimeLocalInput';

function formatVehicleTernary(
  value: string | null | undefined,
  t: (key: string) => string
): string {
  switch (value) {
    case 'yes':
      return t('vehicleCard.general.ternaryYes');
    case 'no':
      return t('vehicleCard.general.ternaryNo');
    case 'n/a':
      return t('vehicleCard.general.ternaryNA');
    case null:
    case undefined:
    case '':
      return '—';
    default:
      return value;
  }
}

const HISTORY_FIELDS_ALREADY_SHOWN = new Set([
  'type',
  'date',
  'km',
  'city',
  'company_name',
  'inspection_type',
  'action',
]);

const HISTORY_FIELD_LABEL: Record<string, string> = {
  active: 'vehicleCard.historyActive',
  is_company: 'vehicleCard.historyIsCompany',
  company_url: 'vehicleCard.historyCompanyUrl',
};

function historyExtraFields(
  event: Record<string, unknown>,
  t: (key: string) => string
): Array<{ key: string; label: string; value: string }> {
  const extras: Array<{ key: string; label: string; value: string }> = [];
  for (const [key, value] of Object.entries(event)) {
    if (HISTORY_FIELDS_ALREADY_SHOWN.has(key) || value == null || value === '') continue;
    if (typeof value === 'object') continue;
    const i18nKey = HISTORY_FIELD_LABEL[key];
    const translated = i18nKey ? t(i18nKey) : '';
    const label = translated && translated !== i18nKey ? translated : key;
    extras.push({ key, label, value: String(value) });
  }
  return extras;
}

const VehicleCard: React.FC<VehicleCardProps> = ({ registrationNumber, vehicleId, onClose }) => {
  const { t } = useTranslation();
  const { hasFeatureAccess, isLoading: permLoading } = usePermissions();
  const isGlobalAdmin = useAuthStore((s) => s.user?.is_global_admin === true);
  const [crossTopNote, setCrossTopNote] = useState('');
  const [crossTopKeepData, setCrossTopKeepData] = useState(true);
  const [crossTopMoveDevice, setCrossTopMoveDevice] = useState<boolean | null>(null);
  const [activeRegistrationNumber, setActiveRegistrationNumber] = useState(registrationNumber);
  const [activeVehicleId, setActiveVehicleId] = useState<number | undefined>(vehicleId);
  const [replacementChain, setReplacementChain] = useState<ReplacementChainResult | null>(null);
  const [mapOpen, setMapOpen] = useState(false);

  useEffect(() => {
    setActiveRegistrationNumber(registrationNumber);
    setActiveVehicleId(vehicleId);
  }, [registrationNumber, vehicleId]);
  
  // All state and logic from custom hook
  const {
    isLoading,
    isRefreshing,
    error,
    vehicle,
    carInfo,
    odometer,
    meta,
    activeTab,
    setActiveTab,
    pickupPlacements,
    showBookingTab,
    availableDevices,
    deviceSearch,
    setDeviceSearch,
    isLoadingDevices,
    showDeviceDropdown,
    setShowDeviceDropdown,
    selectDevice,
    pendingImei,
    pendingNotes,
    setPendingNotes,
    pendingOrganizationId,
    setPendingOrganizationId,
    vehicleCategories,
    isLoadingVehicleCategories,
    pendingVehicleCategoryId,
    setPendingVehicleCategoryId,
    alternativeFuels,
    isLoadingAlternativeFuels,
    pendingAlternativeFuelId,
    setPendingAlternativeFuelId,
    usageStatuses,
    isLoadingUsageStatuses,
    pendingUsageStatusId,
    setPendingUsageStatusId,
    pendingBusinessResponsibilityId,
    setPendingBusinessResponsibilityId,
    pendingReplacesVehicleRegistration,
    setPendingReplacesVehicleRegistration,
    pendingReferenceNumber,
    setPendingReferenceNumber,
    pendingPlacement,
    setPendingPlacement,
    placementFromVehicle,
    pendingCharging,
    setPendingCharging,
    chargingFromVehicle,
    hasChanges,
    isSaving,
    saveError,
    setSaveError,
    saveChanges,
    resetChanges,
    orgMovePending,
    confirmOrgMove,
    cancelOrgMove,
    isEditingOdometer,
    setIsEditingOdometer,
    pendingInitialOdometerKm: _pendingInitialOdometerKm,
    setPendingInitialOdometerKm,
    pendingCurrentOdometerKm,
    setPendingCurrentOdometerKm,
    isSavingOdometer,
    saveOdometer,
    cancelOdometerEdit,
    expandedCategories,
    toggleCategory,
    flinkeyBoxIdInput: _flinkeyBoxIdInput,
    setFlinkeyBoxIdInput: _setFlinkeyBoxIdInput,
    isSavingFlinkey,
    attachFlinkey: _attachFlinkey,
    detachFlinkey: _detachFlinkey,
    showAssignModal,
    setShowAssignModal,
    assignUserId,
    setAssignUserId,
    assignValidFrom,
    setAssignValidFrom,
    assignValidUntil,
    setAssignValidUntil,
    assignFlinkey,
    isAssigningFlinkey,
    linkedFlinkeyBox,
    availableFlinkeyBoxes,
    isLoadingFlinkeyBoxes,
    flinkeyListLoadError,
    fetchFlinkeyBox,
    linkFlinkeyBox,
    unlinkFlinkeyBox,
    fetchDetails,
    fetchAvailableDevices,
    showFlinkeyBoxDropdown,
    setShowFlinkeyBoxDropdown,
    flinkeyBoxSearch,
    setFlinkeyBoxSearch,
  } = useVehicleCard({ registrationNumber: activeRegistrationNumber, vehicleId: activeVehicleId });

  const referenceOrganizationId =
    pendingOrganizationId !== undefined && pendingOrganizationId !== ''
      ? pendingOrganizationId
      : String(vehicle?.organizationId ?? '');
  const referenceNames = useVehicleReferenceNames(referenceOrganizationId || null);

  const loadVehicleMapPosition = useCallback(async (): Promise<VehicleMapPosition> => {
    const id = vehicle?.id;
    if (!id) throw new Error('missing vehicle');
    const res = await api.get<{ data: VehicleMapPosition }>(
      `/api/vehicles/by-id/${encodeURIComponent(id)}/map-position`
    );
    if (!res.data?.data) throw new Error('missing position');
    return res.data.data;
  }, [vehicle?.id]);

  const canViewReplacementChain = !permLoading && hasFeatureAccess('DEQUE_REPLACEMENT_CHAIN');

  const loadReplacementChain = useCallback(async () => {
    if (!canViewReplacementChain) {
      setReplacementChain(null);
      return;
    }
    try {
      const data = await fetchReplacementChain({
        registrationNumber: activeRegistrationNumber,
        vehicleId: activeVehicleId,
      });
      setReplacementChain(data);
    } catch {
      setReplacementChain(null);
    }
  }, [activeRegistrationNumber, activeVehicleId, canViewReplacementChain]);

  useEffect(() => {
    void loadReplacementChain();
  }, [loadReplacementChain]);

  const handleOpenChainVehicle = useCallback((reg: string, id: number) => {
    setActiveRegistrationNumber(reg);
    setActiveVehicleId(id);
  }, []);

  useEffect(() => {
    if (orgMovePending) {
      setCrossTopNote('');
      setCrossTopKeepData(true);
      setCrossTopMoveDevice(null);
    }
  }, [orgMovePending]);

  // Hämta Flinkey-box när fordon laddats
  useEffect(() => {
    if (vehicle?.id) {
      fetchFlinkeyBox();
    }
  }, [vehicle?.id, fetchFlinkeyBox]);

  const filteredFlinkeyBoxes = useMemo(() => {
    const q = flinkeyBoxSearch.trim().toLowerCase();
    if (!q) return availableFlinkeyBoxes;
    return availableFlinkeyBoxes.filter((b) => {
      const hay = [
        b.product_id,
        b.serial_number ?? '',
        b.box_type ?? '',
        b.linked_vehicle_registration ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [availableFlinkeyBoxes, flinkeyBoxSearch]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      if (dateStr.endsWith('-00')) {
        const parts = dateStr.split('-');
        if (parts.length >= 2) {
          const year = parts[0];
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
          const monthIdx = parseInt(parts[1]) - 1;
          if (monthIdx >= 0 && monthIdx < 12) {
            return `${monthNames[monthIdx]} ${year}`;
          }
          return year;
        }
      }
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('sv-SE');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { bg: string; text: string; label: string }> = {
      ACTIVE: { bg: 'bg-green-100 dark:bg-green-950/50', text: 'text-green-800 dark:text-green-200', label: t('vehicleCard.statusActive') },
      MAINTENANCE: { bg: 'bg-yellow-100 dark:bg-yellow-950/40', text: 'text-yellow-800 dark:text-yellow-200', label: t('vehicleCard.statusMaintenance') },
      INACTIVE: { bg: 'bg-gray-100 dark:bg-bgLight', text: 'text-gray-800 dark:text-textDefault', label: t('vehicleCard.statusInactive') },
      RETIRED: { bg: 'bg-red-100 dark:bg-red-950/40', text: 'text-red-800 dark:text-red-200', label: t('vehicleCard.statusDeregistered') },
    };
    const normalizedStatus = (status || '').toUpperCase();
    const style = statusMap[normalizedStatus] || statusMap.INACTIVE;
    return (
      <span className={`px-2 py-0.5 text-xs font-semibold rounded ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  // Hämta färg från Car.info
  const getColor = () => {
    if (carInfo?.colorCodes && carInfo.colorCodes.length > 0) {
      return carInfo.colorCodes.map((c: any) => c.name || c.code).join(', ');
    }
    return '-';
  };

  // Tabbar (bokning visas bara när fordonet ingår i minst en utlämningsplats-pool)
  const tabs = useMemo(() => {
    const rows: { id: TabId; label: string; badge?: boolean }[] = [
      { id: 'general', label: t('vehicleCard.tabGeneral') },
      { id: 'economy', label: t('vehicleCard.tabEconomy') },
      { id: 'cards', label: t('vehicleCard.tabCards') },
      { id: 'basic', label: t('vehicleCard.tabSpec') },
      { id: 'specifications', label: t('vehicleCard.tabProperties') },
      { id: 'placement', label: t('vehicleCard.tabPlacement') },
      { id: 'charging', label: t('charging.tab') },
      { id: 'service', label: t('service.title') },
      { id: 'tires', label: t('service.tires.title') },
      { id: 'history', label: t('vehicleCard.tabHistory') },
    ];
    if (showBookingTab) {
      rows.push({ id: 'booking', label: t('vehicleCard.tabBooking'), badge: true });
    }
    if (isGlobalAdmin) {
      rows.push({ id: 'devicePnP', label: t('vehicleCard.tabDevicePnP') });
    }
    rows.push(
      { id: 'notes', label: t('vehicleCard.tabNotes') },
      { id: 'attachments', label: t('vehicleCard.tabAttachments') },
      { id: 'equipment', label: t('equipment.vehicleEquipment') }
    );
    return rows;
  }, [t, showBookingTab, isGlobalAdmin]);

  // Render grunddata (överst, alltid synlig)
  const renderBasicInfo = () => {
    if (!vehicle) return null;

    return (
      <div className="bg-bgCard border border-border rounded-lg p-4">
        <div className="grid grid-cols-6 gap-4 text-sm">
          {/* Rad 1 */}
          <div>
            <label className="text-textSecondary text-xs">RegNr:</label>
            <div className="font-medium">{registrationNumber}</div>
          </div>
          <div>
            <label className="text-textSecondary text-xs">{t('vehicleCard.brand')}</label>
            <div className="font-medium">{carInfo?.brand || vehicle.brand || '-'}</div>
          </div>
          <div>
            <label className="text-textSecondary text-xs">Modell:</label>
            <div className="font-medium">{carInfo?.salesName || carInfo?.model || vehicle.model || '-'}</div>
          </div>
          <div>
            <label className="text-textSecondary text-xs">{t('vehicleCard.color')}</label>
            <div className="font-medium">{getColor()}</div>
          </div>
          <div>
            <label className="text-textSecondary text-xs">{t('vehicleCard.year')}</label>
            <div className="font-medium">{carInfo?.modelYear || vehicle.year || '-'}</div>
          </div>
          <div>
            <label className="text-textSecondary text-xs">{t('vehicleCard.systemStatus')}</label>
            <div>{getStatusBadge(vehicle.status)}</div>
          </div>

          {/* Referens (samma som fordonslistan), organisation, fordonskategori, ersätter fordon */}
          <div className="col-span-6 md:col-span-3 lg:col-span-2">
            <label className="text-textSecondary text-xs block mb-1">{t('fleet.referenceNumber')}</label>
            <input
              type="text"
              value={
                pendingReferenceNumber !== undefined
                  ? pendingReferenceNumber
                  : (vehicle.referenceNumber ?? '')
              }
              onChange={(e) => setPendingReferenceNumber(e.target.value)}
              list="vehicle-card-reference-values"
              placeholder={t('fleet.referenceChooseOrType')}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgLight text-textDefault focus:outline-none focus:ring-2 focus:ring-primary"
              maxLength={100}
            />
            <datalist id="vehicle-card-reference-values">
              {referenceNames.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>
          <div className="col-span-6 md:col-span-3 lg:col-span-2">
            <label className="text-textSecondary text-xs block mb-1">
              {t('vehicleCard.organizationGroup')}
            </label>
            <OrgTreeSelect
              value={
                pendingOrganizationId !== undefined
                  ? pendingOrganizationId
                  : String(vehicle.organizationId ?? '')
              }
              onChange={setPendingOrganizationId}
              placeholder={t('fleet.addVehicle.selectOrg')}
              showEmptyOption={false}
              className="text-sm"
            />
          </div>
          <div className="col-span-6 md:col-span-3 lg:col-span-1">
            <label className="text-textSecondary text-xs block mb-1">
              {t('vehicleCard.vehicleCategory')}
            </label>
            <select
              value={
                pendingVehicleCategoryId !== undefined
                  ? pendingVehicleCategoryId
                  : String(vehicle.vehicleCategoryId ?? '')
              }
              onChange={(e) => setPendingVehicleCategoryId(e.target.value)}
              disabled={isLoadingVehicleCategories}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgLight text-textDefault focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">{t('vehicleCard.noVehicleCategory')}</option>
              {vehicleCategories.map((category) => (
                <option key={category.id} value={String(category.id)}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-6 md:col-span-3 lg:col-span-1">
            <label className="text-textSecondary text-xs block mb-1">
              {t('vehicleCard.replacesVehicleReg')}
            </label>
            <input
              type="text"
              value={
                pendingReplacesVehicleRegistration !== undefined
                  ? pendingReplacesVehicleRegistration
                  : (vehicle.replacesVehicleRegistration ?? '')
              }
              onChange={(e) => setPendingReplacesVehicleRegistration(e.target.value.toUpperCase())}
              placeholder={t('vehicleCard.replacesVehicleRegPlaceholder')}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgLight text-textDefault focus:outline-none focus:ring-2 focus:ring-primary uppercase"
              maxLength={32}
            />
          </div>

          {/* Rad 2 – tekniskt (Car.info); 7 fält: VIN + bränsle + motor + växellåda + effekt + typ */}
          <div className="col-span-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
            <div className="min-w-0 lg:col-span-2">
              <label className="text-textSecondary text-xs">VIN:</label>
              <div className="font-medium truncate" title={carInfo?.vin || vehicle?.vin || undefined}>{carInfo?.vin || vehicle?.vin || '-'}</div>
            </div>
            <div className="min-w-0">
              <label className="text-textSecondary text-xs">{t('vehicleCard.fuelType')}</label>
              <div className="font-medium truncate" title={carInfo?.engineType || formatFuelTypeLabel(vehicle.fuelType) || undefined}>
                {carInfo?.engineType || formatFuelTypeLabel(vehicle.fuelType) || '-'}
              </div>
            </div>
            <div className="min-w-0">
              <label className="text-textSecondary text-xs">Motor:</label>
              <div className="font-medium truncate" title={carInfo?.engineName || undefined}>
                {carInfo?.engineName || '-'}
              </div>
            </div>
            <div className="min-w-0">
              <label className="text-textSecondary text-xs">{t('vehicleCard.gearbox')}</label>
              <div
                className="font-medium truncate"
                title={(carInfo?.transmission || vehicle?.transmission) || undefined}
              >
                {carInfo?.transmission || vehicle?.transmission || '-'}
              </div>
            </div>
            <div className="min-w-0">
              <label className="text-textSecondary text-xs">Effekt:</label>
              <div className="font-medium">{carInfo?.horsepower ? `${carInfo.horsepower} hk` : '-'}</div>
            </div>
            <div className="min-w-0">
              <label className="text-textSecondary text-xs">Fordonstyp:</label>
              <div className="font-medium truncate" title={carInfo?.vehicleType || undefined}>
                {carInfo?.vehicleType || '-'}
              </div>
            </div>
          </div>

          {/* Rad 3 – flinkey (digital nyckel) — välj box som enhet: alla org-boxar + sök */}
          <div className="col-span-6 border-t border-border pt-3 mt-2 relative">
            <label className="text-textSecondary text-xs block mb-1">{t('flinkey.sectionTitle') || 'flinkey (digital nyckel)'}</label>
            {isLoadingFlinkeyBoxes ? (
              <span className="text-xs text-textTertiary">{t('common.loading')}</span>
            ) : flinkeyListLoadError !== null ? (
              <span className="text-xs text-amber-700 dark:text-amber-300">
                {flinkeyListLoadError.trim() ? flinkeyListLoadError : t('flinkey.listLoadError')}
              </span>
            ) : availableFlinkeyBoxes.length === 0 ? (
              <span className="text-xs text-textTertiary">{t('flinkey.noBoxesForOrg')}</span>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="text-[11px] text-textTertiary">
                  {t('flinkey.orgBoxCount', { count: availableFlinkeyBoxes.length })}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  {linkedFlinkeyBox ? (
                    <>
                      <span className="font-medium text-sm">{linkedFlinkeyBox.product_id}</span>
                      {linkedFlinkeyBox.box_type ? (
                        <span className="text-xs text-textTertiary">{linkedFlinkeyBox.box_type}</span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => unlinkFlinkeyBox(linkedFlinkeyBox.id)}
                        disabled={isSavingFlinkey}
                        className="text-red-600 hover:text-red-800 text-xs underline disabled:opacity-50"
                      >
                        {t('flinkey.detach') || 'Ta bort koppling'}
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-textSecondary">{t('flinkey.noBoxLinked')}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setShowFlinkeyBoxDropdown(!showFlinkeyBoxDropdown);
                      if (!showFlinkeyBoxDropdown) setFlinkeyBoxSearch('');
                    }}
                    disabled={isSavingFlinkey}
                    className="text-primary hover:text-primary/80 text-xs underline disabled:opacity-50"
                  >
                    {linkedFlinkeyBox ? t('flinkey.changeBox') : t('flinkey.chooseBox')}
                  </button>
                </div>

                {showFlinkeyBoxDropdown && (
                  <div className="absolute left-0 top-full mt-1 w-full max-w-md bg-bgElevated border border-border rounded-lg shadow-xl z-50">
                    <div className="p-2 border-b border-border">
                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-textTertiary" />
                        <input
                          type="text"
                          placeholder={t('flinkey.searchBoxesPlaceholder')}
                          value={flinkeyBoxSearch}
                          onChange={(e) => setFlinkeyBoxSearch(e.target.value)}
                          className="w-full pl-8 pr-3 py-1.5 border border-border rounded text-sm bg-bgBase text-textDefault focus:ring-1 focus:ring-primary focus:border-primary"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                      {filteredFlinkeyBoxes.length === 0 ? (
                        <div className="p-3 text-center text-textSecondary text-xs">{t('flinkey.searchNoHits')}</div>
                      ) : (
                        <div className="divide-y divide-border">
                          {filteredFlinkeyBoxes.map((b) => {
                            const onThisVehicle =
                              vehicle?.id != null &&
                              b.linked_vehicle_id != null &&
                              Number(b.linked_vehicle_id) === Number(vehicle.id);
                            const onOtherVehicle =
                              b.linked_vehicle_id != null &&
                              vehicle?.id != null &&
                              Number(b.linked_vehicle_id) !== Number(vehicle.id);
                            return (
                              <button
                                key={b.id}
                                type="button"
                                disabled={isSavingFlinkey || onThisVehicle}
                                onClick={async () => {
                                  if (onThisVehicle) return;
                                  await linkFlinkeyBox(b.id);
                                  setShowFlinkeyBoxDropdown(false);
                                  setFlinkeyBoxSearch('');
                                }}
                                className={`w-full px-3 py-2 text-left transition-colors text-sm ${
                                  onThisVehicle
                                    ? 'bg-BgPrimaryLight/40 text-textDefault cursor-default'
                                    : 'hover:bg-bgLight text-textDefault'
                                } disabled:opacity-50`}
                              >
                                <div className="font-medium flex flex-wrap items-center gap-2">
                                  <span>{b.product_id}</span>
                                  {b.box_type ? (
                                    <span className="text-xs font-normal text-textSecondary">{b.box_type}</span>
                                  ) : null}
                                  {onThisVehicle ? (
                                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0 rounded bg-emerald-600/90 text-white">
                                      {t('flinkey.currentBoxBadge')}
                                    </span>
                                  ) : null}
                                </div>
                                {onOtherVehicle && b.linked_vehicle_registration ? (
                                  <div className="text-xs text-amber-800 dark:text-amber-200 mt-0.5">
                                    {t('flinkey.boxOnOtherVehicle', { reg: b.linked_vehicle_registration })}
                                  </div>
                                ) : !b.linked_vehicle_id ? (
                                  <div className="text-xs text-textTertiary mt-0.5">{t('flinkey.boxUnassigned')}</div>
                                ) : null}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="p-1.5 border-t border-border bg-bgBase">
                      <button
                        type="button"
                        onClick={() => {
                          setShowFlinkeyBoxDropdown(false);
                          setFlinkeyBoxSearch('');
                        }}
                        className="w-full px-2 py-1 text-xs text-textSecondary hover:bg-bgLight rounded"
                      >
                        {t('common.close')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Rad 4 */}
          <div className="relative col-span-2">
            <label className="text-textSecondary text-xs">IMEI:</label>
            <div className="flex items-center gap-2">
              <span className={`font-medium ${pendingImei !== undefined ? 'text-orange-600' : ''}`}>
                {pendingImei !== undefined 
                  ? (pendingImei || 'Ingen enhet') 
                  : (vehicle.imei || '-')}
              </span>
              {pendingImei !== undefined && vehicle.imei !== pendingImei && (
                <span className="text-xs text-textTertiary">({t('copy.ui.changed')})</span>
              )}
              <button
                onClick={() => {
                  setShowDeviceDropdown(!showDeviceDropdown);
                  if (!showDeviceDropdown) {
                    fetchAvailableDevices('');
                  }
                }}
                className="text-primary hover:text-primary/80 text-xs underline"
              >
                {vehicle.imei || pendingImei ? t('vehicleCard.changeDevice') : t('vehicleCard.selectDevice')}
              </button>
              {(vehicle.imei || pendingImei) && (
                <button
                  onClick={() => selectDevice(null)}
                  className="text-red-500 hover:text-red-700 text-xs"
                  title={t('vehicleCard.removeDeviceLink')}
                >
                  ✕
                </button>
              )}
            </div>
            
            {/* Dropdown för att välja enhet */}
            {showDeviceDropdown && (
              <div className="absolute left-0 top-full mt-1 w-72 bg-bgElevated border border-border rounded-lg shadow-xl z-50">
                <div className="p-2 border-b border-border">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-textTertiary" />
                    <input
                      type="text"
                      placeholder={t('vehicleCard.searchImei')}
                      value={deviceSearch}
                      onChange={(e) => setDeviceSearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 border border-border rounded text-sm bg-bgBase text-textDefault focus:ring-1 focus:ring-primary focus:border-primary"
                      autoFocus
                    />
                  </div>
                </div>
                
                <div className="max-h-48 overflow-y-auto">
                  {isLoadingDevices ? (
                    <div className="p-3 text-center text-textSecondary text-sm">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mx-auto"></div>
                    </div>
                  ) : availableDevices.length === 0 ? (
                    <div className="p-3 text-center text-textSecondary text-xs">
                      Inga lediga enheter
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {availableDevices.map((device) => (
                        <button
                          key={device.id}
                          onClick={() => selectDevice(device.imei)}
                          className="w-full px-3 py-2 text-left hover:bg-bgLight transition-colors text-sm text-textDefault"
                        >
                          <div className="font-medium">{device.imei}</div>
                          {device.organization_name && (
                            <div className="text-xs text-textSecondary">{device.organization_name}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="p-1.5 border-t border-border bg-bgBase">
                  <button
                    onClick={() => {
                      setShowDeviceDropdown(false);
                      setDeviceSearch('');
                    }}
                    className="w-full px-2 py-1 text-xs text-textSecondary hover:bg-bgLight rounded"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="text-textSecondary text-xs">Senast ansluten:</label>
            <div className="font-medium">
              {vehicle.lastStatusUpdate ? new Date(vehicle.lastStatusUpdate).toLocaleString('sv-SE') : '-'}
            </div>
          </div>
          <div>
            <label className="text-textSecondary text-xs">Registrerad:</label>
            <div className="font-medium">{formatDate(carInfo?.registerDate)}</div>
          </div>
          <div>
            <label className="text-textSecondary text-xs">Tillverkad:</label>
            <div className="font-medium">{formatDate(carInfo?.manufacturedDate)}</div>
          </div>
          <div>
            <label className="text-textSecondary text-xs flex items-center gap-1">
              Stulen:
              {carInfo?.isStolen && <span className="text-red-600">⚠️</span>}
            </label>
            <div className={`font-medium ${carInfo?.isStolen ? 'text-red-600' : ''}`}>
              {carInfo?.isStolen ? 'JA!' : 'Nej'}
            </div>
          </div>
          <div>
            <label className="text-textSecondary text-xs">{t('vehicleCard.general.emergencyVehicle')}</label>
            <div className="font-medium">
              {formatVehicleTernary(carInfo?.emergencyVehicle ?? vehicle?.emergencyVehicle, t)}
            </div>
          </div>
          <div>
            <label className="text-textSecondary text-xs flex items-center gap-1">
              {t('vehicleCard.general.hasDrivingBan')}
              {(carInfo?.hasDrivingBan === 'yes' || vehicle?.hasDrivingBan === 'yes') && (
                <span className="text-red-600">⚠️</span>
              )}
            </label>
            <div
              className={`font-medium ${
                carInfo?.hasDrivingBan === 'yes' || vehicle?.hasDrivingBan === 'yes' ? 'text-red-600' : ''
              }`}
            >
              {formatVehicleTernary(carInfo?.hasDrivingBan ?? vehicle?.hasDrivingBan, t)}
            </div>
          </div>

          {/* Rad 4 - Mätarställningar (redigerbara) */}
          <div className="col-span-6 border-t border-border pt-3 mt-2">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-textSecondary text-xs flex items-center gap-1">
                  Start odometer:
                  {odometer?.hasOem && (
                    <span className="text-[10px] text-green-600 ml-1" title={t('vehicleCard.odometerFromOem')}>OEM</span>
                  )}
                </label>
                <div className="font-medium">
                  {isEditingOdometer && pendingCurrentOdometerKm !== null && !vehicle.initialOdometer
                    ? (() => {
                        const driven = odometer?.totalDrivenKm || 0;
                        const newInitial = Math.max(0, (pendingCurrentOdometerKm || 0) - driven);
                        return (
                          <span className="text-xs text-textSecondary italic">
                            {newInitial.toLocaleString('sv-SE')} km <span className="text-textTertiary">({t('copy.ui.calculated')})</span>
                          </span>
                        );
                      })()
                    : vehicle.initialOdometer
                      ? `${Math.round(vehicle.initialOdometer / 1000).toLocaleString('sv-SE')} km`
                      : '-'
                  }
                </div>
              </div>
              <div>
                <label className="text-textSecondary text-xs">{t('vehicleCard.distanceDriven')}</label>
                <div className="font-medium">
                  {odometer && odometer.totalDrivenKm > 0
                    ? `${odometer.totalDrivenKm.toLocaleString('sv-SE')} km`
                    : '-'}
                </div>
              </div>
              <div>
                <label className="text-textSecondary text-xs flex items-center gap-1">
                  {t('vehicleCard.currentOdometer')}
                  {!isEditingOdometer && !odometer?.hasOem && (
                    <button
                      onClick={() => {
                        setIsEditingOdometer(true);
                        setPendingCurrentOdometerKm(odometer?.currentKm || 0);
                        setPendingInitialOdometerKm(null);
                      }}
                      className="text-primary hover:underline text-xs ml-1"
                    >
                      {t('copy.ui.change')}
                    </button>
                  )}
                </label>
                {isEditingOdometer ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={pendingCurrentOdometerKm ?? ''}
                      onChange={(e) => setPendingCurrentOdometerKm(parseInt(e.target.value) || 0)}
                      className="w-24 px-2 py-1 text-sm border border-primary rounded bg-bgBase text-textDefault focus:ring-1 focus:ring-primary"
                      min={0}
                    />
                    <span className="text-xs text-textSecondary">km</span>
                  </div>
                ) : (
                  <div className="font-medium">
                    {odometer?.currentKm ? `${odometer.currentKm.toLocaleString('sv-SE')} km` : '-'}
                  </div>
                )}
              </div>
            </div>
            
            {/* Spara/Avbryt-knappar för mätarställning */}
            {isEditingOdometer && (
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={saveOdometer}
                  disabled={isSavingOdometer}
                  className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 disabled:opacity-50 flex items-center gap-1"
                >
                  {isSavingOdometer ? (
                    <>
                      <ArrowPathIcon className="w-3 h-3 animate-spin" />
                      Sparar...
                    </>
                  ) : (
                    t('vehicleCard.saveOdometer')
                  )}
                </button>
                <button
                  onClick={cancelOdometerEdit}
                  className="px-3 py-1 bg-bgLight text-textDefault text-sm rounded hover:bg-bgLayout"
                >
                  Avbryt
                </button>
                <span className="text-xs text-textSecondary ml-2">
                  💡 {t('vehicleCard.tripsRecalculated')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render historik
  const renderHistory = () => {
    const history = carInfo?.history;
    if (!history || history.length === 0) {
      return <div className="text-textSecondary text-center py-8">{t('vehicleCard.noHistory')}</div>;
    }

    const typeIcons: Record<string, string> = {
      inspection: '🔍',
      owner: '👤',
      traffic: '🚗',
      registry: '📋',
      leasing: '📝',
      classified_archive: '💰',
    };

    const typeNames: Record<string, string> = {
      inspection: 'Besiktning',
      owner: t('vehicleCard.ownerChange'),
      traffic: 'Trafikregistrering',
      registry: 'Registrering',
      leasing: 'Leasing',
      classified_archive: 'Annons',
    };

    const sortedHistory = [...history].sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

    return (
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {sortedHistory.map((event: any, idx: number) => (
          <div key={idx} className="flex items-start gap-3 p-3 bg-bgBase rounded-lg text-sm">
            <span className="text-lg">{typeIcons[event.type] || '📌'}</span>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <span className="font-medium text-textDefault">
                  {event.type === 'leasing'
                    ? isCarInfoLeasingActive(event.active)
                      ? t('fleet.ownershipLeased')
                      : t('fleet.ownershipLeaseEnded')
                    : typeNames[event.type] || event.type}
                  {event.inspection_type && ` - ${event.inspection_type}`}
                  {event.action && ` (${event.action})`}
                </span>
                <span className="text-textSecondary">{formatDate(event.date)}</span>
              </div>
              <div className="text-textSecondary text-xs mt-0.5">
                {event.km !== undefined && <span>Km: {event.km.toLocaleString('sv-SE')} • </span>}
                {event.city && <span>{event.city} • </span>}
                {event.company_name && <span>{event.company_name}</span>}
                {historyExtraFields(event, t).map((extra) => (
                  <span key={extra.key}> • {extra.label}: {extra.value}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render anteckningar
  const renderNotes = () => {
    const currentNotes = pendingNotes !== undefined ? pendingNotes : (vehicle?.notes || '');
    const hasNotesChanges = pendingNotes !== undefined && pendingNotes !== (vehicle?.notes || '');

    return (
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-textDefault">{t('vehicleCard.tabNotes')}</h4>
            {hasNotesChanges && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPendingNotes(undefined)}
                  className="px-3 py-1 text-sm text-textSecondary hover:text-textDefault border border-border rounded"
                >
                  Avbryt
                </button>
                <button
                  onClick={saveChanges}
                  disabled={isSaving}
                  className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                >
                  {isSaving ? 'Sparar...' : 'Spara'}
                </button>
              </div>
            )}
          </div>
          <textarea
            className={`w-full border border-border rounded-lg p-3 h-48 text-sm bg-bgBase text-textDefault focus:ring-2 focus:ring-primary focus:border-primary ${
              hasNotesChanges ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-600' : ''
            }`}
            value={currentNotes}
            onChange={(e) => setPendingNotes(e.target.value)}
            placeholder={t('vehicleCard.writeNotesHere')}
          />
          {saveError && activeTab === 'notes' && (
            <p className="text-red-500 text-sm mt-2">{saveError}</p>
          )}
        </div>
      </div>
    );
  };

  // Render aktivt tab-innehåll
  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return vehicle?.id && (vehicle?.organizationId ?? (vehicle as any)?.organization_id) ? (
          <VehicleGeneralInfoTab
            vehicleId={parseInt(vehicle.id, 10)}
            organizationId={
              pendingOrganizationId !== undefined
                ? pendingOrganizationId
                : String(vehicle.organizationId ?? (vehicle as any).organization_id ?? '')
            }
            carInfo={carInfo}
            alternativeFuels={alternativeFuels}
            isLoadingAlternativeFuels={isLoadingAlternativeFuels}
            alternativeFuelId={
              pendingAlternativeFuelId !== undefined
                ? pendingAlternativeFuelId
                : String(vehicle.alternativeFuelId ?? '')
            }
            onAlternativeFuelChange={setPendingAlternativeFuelId}
            usageStatuses={usageStatuses}
            isLoadingUsageStatuses={isLoadingUsageStatuses}
            usageStatusId={
              pendingUsageStatusId !== undefined
                ? pendingUsageStatusId
                : String(vehicle.usageStatusId ?? '')
            }
            onUsageStatusChange={setPendingUsageStatusId}
            businessResponsibilityId={
              pendingBusinessResponsibilityId !== undefined
                ? pendingBusinessResponsibilityId
                : String(vehicle.businessResponsibilityId ?? '')
            }
            onBusinessResponsibilityChange={setPendingBusinessResponsibilityId}
            businessResponsibilityLabel={
              vehicle.businessResponsibilityAnsvarId && vehicle.businessResponsibilityAnsvarText
                ? `${vehicle.businessResponsibilityAnsvarId} - ${vehicle.businessResponsibilityAnsvarText}`
                : null
            }
            businessResponsibilityIkpartLabel={
              vehicle.businessResponsibilityIkpartId && vehicle.businessResponsibilityIkpartText
                ? `${vehicle.businessResponsibilityIkpartId} - ${vehicle.businessResponsibilityIkpartText}`
                : vehicle.businessResponsibilityIkpartId || vehicle.businessResponsibilityIkpartText
                  ? [vehicle.businessResponsibilityIkpartId, vehicle.businessResponsibilityIkpartText]
                      .filter(Boolean)
                      .join(' - ')
                  : null
            }
            tempFordonsansvarigRsid={vehicle.tempFordonsansvarigRsid}
            tempFordonsansvarigName={vehicle.tempFordonsansvarigName}
            tempFordonsansvarigEmail={vehicle.tempFordonsansvarigEmail}
            tempFordonsansvarigPhone={vehicle.tempFordonsansvarigPhone}
          />
        ) : null;
      case 'basic':
        return activeVehicleId ? <VehicleFuelTransactionsTab vehicleId={activeVehicleId} /> : null;
      case 'specifications':
        return (
          <VehicleSpecificationsTab
            key={vehicle?.id ?? registrationNumber}
            specifications={carInfo?.specifications}
            basicAttributes={carInfo?.basicAttributes}
            extendedAttributes={carInfo?.extendedAttributes}
            packages={carInfo?.packages}
            tiresInfo={carInfo?.tiresInfo}
            alerts={carInfo?.alerts}
            expandedCategories={expandedCategories}
            onToggleCategory={toggleCategory}
          />
        );
      case 'placement':
        return vehicle ? (
          <VehiclePlacementTab
            value={
              pendingPlacement ??
              placementFromVehicle(vehicle)
            }
            onChange={setPendingPlacement}
            hasChanges={
              pendingPlacement !== undefined &&
              !(
                pendingPlacement.address.trim() === (vehicle.placementAddress ?? '').trim() &&
                pendingPlacement.postalCode.trim() === (vehicle.placementPostalCode ?? '').trim() &&
                pendingPlacement.city.trim() === (vehicle.placementCity ?? '').trim() &&
                pendingPlacement.latitude === (vehicle.placementLatitude ?? null) &&
                pendingPlacement.longitude === (vehicle.placementLongitude ?? null)
              )
            }
            lastPosition={
              vehicle.lastPosition?.lat != null && vehicle.lastPosition?.lng != null
                ? {
                    lat: vehicle.lastPosition.lat,
                    lng: vehicle.lastPosition.lng,
                    timestamp:
                      typeof vehicle.lastPosition.timestamp === 'string'
                        ? vehicle.lastPosition.timestamp
                        : vehicle.lastPosition.timestamp
                          ? new Date(vehicle.lastPosition.timestamp).toISOString()
                          : undefined,
                  }
                : null
            }
            onSave={() => void saveChanges()}
            onCancel={resetChanges}
            isSaving={isSaving}
          />
        ) : null;
      case 'charging':
        return vehicle ? (
          <VehicleChargingTab
            value={pendingCharging ?? chargingFromVehicle(vehicle)}
            onChange={setPendingCharging}
            fallbackCenter={
              vehicle.placementLatitude != null && vehicle.placementLongitude != null
                ? { lat: vehicle.placementLatitude, lng: vehicle.placementLongitude }
                : null
            }
            suggestedBatteryCapacityKwh={extractBatteryCapacityKwh(carInfo)}
            batteryCapacitySource={vehicle.batteryCapacitySource ?? null}
            hasChanges={
              pendingCharging !== undefined &&
              !(
                pendingCharging.batteryCapacityKwh === (vehicle.batteryCapacityKwh ?? null) &&
                pendingCharging.chargingEfficiency === (vehicle.chargingEfficiency ?? null) &&
                pendingCharging.homeLatitude === (vehicle.homeLatitude ?? null) &&
                pendingCharging.homeLongitude === (vehicle.homeLongitude ?? null) &&
                pendingCharging.homeRadiusM === (vehicle.homeRadiusM ?? null) &&
                pendingCharging.homeAddress === (vehicle.homeAddress ?? null) &&
                pendingCharging.workLatitude === (vehicle.workLatitude ?? null) &&
                pendingCharging.workLongitude === (vehicle.workLongitude ?? null) &&
                pendingCharging.workRadiusM === (vehicle.workRadiusM ?? null) &&
                pendingCharging.workAddress === (vehicle.workAddress ?? null)
              )
            }
          />
        ) : null;
      case 'history':
        return renderHistory();
      case 'booking':
        return pickupPlacements.length > 0 && vehicle?.id ? (
          <VehicleBookingInfoTab
            vehicleId={parseInt(vehicle.id, 10)}
            placements={pickupPlacements}
            registrationNumber={registrationNumber}
            transmissionDisplay={vehicle.transmission ?? carInfo?.transmission ?? null}
            initialBookingDescription={vehicle.bookingDescription ?? null}
            initialBookingConfirmationEmailNote={vehicle.bookingConfirmationEmailNote ?? null}
            initialBookingRestMinutes={vehicle.bookingRestMinutes ?? null}
            linkedFlinkeyProductId={linkedFlinkeyBox?.product_id ?? null}
            linkedFlinkeyBoxType={linkedFlinkeyBox?.box_type ?? null}
            onSaved={() => void fetchDetails(false)}
          />
        ) : null;
      case 'notes':
        return renderNotes();
      case 'economy':
        return vehicle?.id && (vehicle?.organizationId ?? (vehicle as any)?.organization_id) ? (
          <VehicleEconomyTab
            vehicleId={parseInt(vehicle.id, 10)}
            organizationId={vehicle.organizationId ?? (vehicle as any).organization_id}
          />
        ) : (
          <div className="text-center py-8 text-textSecondary">
            <p className="text-sm">{t('common.loading')}</p>
          </div>
        );
      case 'cards':
        return activeVehicleId ? <VehicleFuelCardsTab vehicleId={activeVehicleId} /> : null;
      case 'service':
        return vehicle?.id ? <VehicleServiceTab vehicleId={parseInt(vehicle.id)} /> : null;
      case 'tires':
        return vehicle?.id ? <VehicleTiresTab vehicleId={parseInt(vehicle.id)} /> : null;
      case 'attachments':
        return <VehicleAttachments vehicleId={parseInt(vehicle?.id || '0')} />;
      case 'equipment':
        return vehicle?.id ? (
          <VehicleEquipmentSection
            vehicleId={parseInt(vehicle.id)}
            organizationId={vehicle.organizationId ?? (vehicle as any).organization_id}
          />
        ) : null;
      case 'devicePnP':
        return isGlobalAdmin && vehicle?.id ? (
          <VehicleDevicePnPTab vehicleId={parseInt(vehicle.id, 10)} />
        ) : null;
      default: {
        const _exhaustive: never = activeTab;
        void _exhaustive;
        return null;
      }
    }
  };

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 100000 }}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-bgElevated text-textDefault rounded-xl shadow-2xl w-full h-[90vh] overflow-hidden flex flex-col border border-border" style={{ maxWidth: '1900px' }}>
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary/80 px-6 py-3 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TruckIcon className="h-7 w-7" />
                <div>
                  <h2 className="text-xl font-bold tracking-wider">{activeRegistrationNumber}</h2>
                  {vehicle && (
                    <p className="text-sm text-white/80">
                      {carInfo?.carName || `${carInfo?.brand || vehicle.brand || ''} ${carInfo?.model || vehicle.model || ''}`.trim()}
                    </p>
                  )}
                  {canViewReplacementChain && replacementChain && replacementChain.nodes.length > 1 && (
                    <VehicleReplacementChainBar
                      nodes={replacementChain.nodes}
                      currentIndex={replacementChain.currentIndex}
                      compact
                      onOpenVehicle={handleOpenChainVehicle}
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {vehicle?.id && (
                  <button
                    type="button"
                    onClick={() => setMapOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-primary shadow-sm hover:bg-white/90"
                  >
                    <MapPinIcon className="h-4 w-4" />
                    {t('booking.vehicleMapShow')}
                  </button>
                )}
                {!permLoading && hasFeatureAccess('DEQUE_VEHICLE_LOG') && vehicle?.id && (
                  <Link
                    to={`${ROUTES.DEQUE_VEHICLE_LOG}?registration_number=${encodeURIComponent(activeRegistrationNumber)}&type=all`}
                    className="text-xs text-white/90 hover:underline mr-1"
                  >
                    {t('vehicleCard.viewDequeVehicleLog')}
                  </Link>
                )}
                {meta.fromCache && meta.cacheAge !== null && !hasChanges && (
                  <span className="text-xs text-white/60 mr-2">
                    Car.info cachad ({meta.cacheAge.toFixed(1)}h)
                  </span>
                )}
                
                {/* Spara/Avbryt-knappar visas när det finns ändringar */}
                {hasChanges && (
                  <>
                    <span className="text-xs text-yellow-200 mr-2">{t('vehicleCard.unsavedChanges')}</span>
                    <button
                      onClick={resetChanges}
                      disabled={isSaving}
                      className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded text-sm transition-colors disabled:opacity-50"
                    >
                      Avbryt
                    </button>
                    <button
                      onClick={saveChanges}
                      disabled={isSaving}
                      className="px-3 py-1.5 bg-green-500 hover:bg-green-600 rounded text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
                    >
                      {isSaving ? (
                        <>
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          Sparar...
                        </>
                      ) : (
                        'Spara'
                      )}
                    </button>
                  </>
                )}
                
                {!hasChanges && (
                  <button
                    onClick={() => fetchDetails(true)}
                    disabled={isRefreshing}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-50"
                    title={t('common.refresh')}
                  >
                    <ArrowPathIcon className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                )}
                
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto flex-1 bg-bgElevated">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-500">{error}</p>
                <button
                  onClick={() => fetchDetails()}
                  className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  {t('vehicleCard.tryAgain')}
                </button>
              </div>
            ) : vehicle ? (
              <div className="space-y-4">
                {/* Save error */}
                {saveError && (
                  <div className="bg-red-100 dark:bg-red-950/40 border-l-4 border-red-500 p-3 rounded flex justify-between items-center">
                    <span className="text-red-700 dark:text-red-300">{saveError}</span>
                    <button onClick={() => setSaveError(null)} className="text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100">✕</button>
                  </div>
                )}
                
                {/* Stolen warning */}
                {carInfo?.isStolen && (
                  <div className="bg-red-100 dark:bg-red-950/40 border-l-4 border-red-500 p-3 rounded">
                    <span className="text-red-700 dark:text-red-300 font-bold">⚠️ {t('vehicleCard.vehicleReportedStolen')}</span>
                  </div>
                )}
                {(carInfo?.hasDrivingBan === 'yes' || vehicle.hasDrivingBan === 'yes') && (
                  <div className="bg-red-100 dark:bg-red-950/40 border-l-4 border-red-500 p-3 rounded">
                    <span className="text-red-700 dark:text-red-300 font-bold">⚠️ {t('vehicleCard.drivingBanWarning')}</span>
                  </div>
                )}

                {/* Grunddata (alltid synlig) */}
                {renderBasicInfo()}

                {/* Tabbar */}
                <div className="border-b border-border">
                  <div className="flex gap-1 overflow-x-auto flex-nowrap pb-0.5">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`shrink-0 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'border-primary text-primary bg-BgPrimaryLight'
                            : 'border-transparent text-textSecondary hover:text-textDefault hover:bg-bgLight'
                        }`}
                      >
                        <span>{tab.label}</span>
                        {tab.badge ? (
                          <span className="ml-1.5 align-middle rounded bg-primary/20 px-1 py-0.5 text-[10px] font-bold uppercase text-primary">
                            {t('vehicleCard.tabBookingBadge')}
                          </span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab-innehåll */}
                <div className="py-4">
                  {renderTabContent()}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* flinkey – Tilldela digital nyckel modal */}
      {orgMovePending && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !isSaving && cancelOrgMove()}
        >
          <div
            className="bg-bgElevated text-textDefault border border-border rounded-lg shadow-xl p-6 max-w-lg w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2 text-textDefault">{t('vehicleCard.orgMoveTitle')}</h3>
            <p className="text-sm text-textSecondary mb-4">
              {orgMovePending.isCrossTop
                ? t('vehicleCard.crossTopBody')
                : t('vehicleCard.orgMoveSameTopBody')}
            </p>
            <fieldset className="mb-4 space-y-3">
              <legend className="block text-sm font-medium text-textDefault mb-1">
                {t('vehicleCard.crossTopKeepDataLabel')}
              </legend>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="orgMoveDataRetention"
                  checked={crossTopKeepData}
                  onChange={() => setCrossTopKeepData(true)}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium text-textDefault">
                    {t('vehicleCard.crossTopKeepDataLabel')}
                  </span>
                  <span className="block text-xs text-textSecondary">
                    {t('vehicleCard.crossTopKeepDataHelp')}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="orgMoveDataRetention"
                  checked={!crossTopKeepData}
                  onChange={() => setCrossTopKeepData(false)}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium text-textDefault">
                    {t('vehicleCard.crossTopStartFreshLabel')}
                  </span>
                  <span className="block text-xs text-textSecondary">
                    {t('vehicleCard.crossTopStartFreshHelp')}
                  </span>
                </span>
              </label>
            </fieldset>
            {orgMovePending.isCrossTop && (
              <fieldset className="mb-4 space-y-3">
                <legend className="block text-sm font-medium text-textDefault mb-1">
                  {t('vehicleCard.crossTopMoveDeviceLabel')}
                </legend>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="crossTopMoveDevice"
                    checked={crossTopMoveDevice === true}
                    onChange={() => setCrossTopMoveDevice(true)}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-medium text-textDefault">
                      {t('vehicleCard.crossTopMoveDeviceYes')}
                    </span>
                    <span className="block text-xs text-textSecondary">
                      {t('vehicleCard.crossTopMoveDeviceYesHelp')}
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="crossTopMoveDevice"
                    checked={crossTopMoveDevice === false}
                    onChange={() => setCrossTopMoveDevice(false)}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-medium text-textDefault">
                      {t('vehicleCard.crossTopMoveDeviceNo')}
                    </span>
                    <span className="block text-xs text-textSecondary">
                      {t('vehicleCard.crossTopMoveDeviceNoHelp')}
                    </span>
                  </span>
                </label>
                {crossTopMoveDevice === null && (
                  <p className="text-xs text-amber-700">{t('vehicleCard.crossTopMoveDeviceRequired')}</p>
                )}
              </fieldset>
            )}
            <label className="block text-sm text-textSecondary mb-1">{t('vehicleCard.crossTopNoteLabel')}</label>
            <textarea
              value={crossTopNote}
              onChange={(e) => setCrossTopNote(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded bg-bgBase text-textDefault text-sm mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={isSaving}
                onClick={() => cancelOrgMove()}
                className="px-4 py-2 border border-border rounded hover:bg-bgLight text-textDefault disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={isSaving || (orgMovePending.isCrossTop && crossTopMoveDevice === null)}
                onClick={() =>
                  !(orgMovePending.isCrossTop && crossTopMoveDevice === null) &&
                  confirmOrgMove(crossTopNote, {
                    keepVehicleData: crossTopKeepData,
                    moveDevice: orgMovePending.isCrossTop ? Boolean(crossTopMoveDevice) : true,
                  })
                }
                className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
              >
                {isSaving ? t('common.saving') : t('vehicleCard.crossTopConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignModal && vehicle?.id && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => setShowAssignModal(false)}>
          <div className="bg-bgElevated text-textDefault border border-border rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4 text-textDefault">{t('flinkey.assignModalTitle') || 'Tilldela digital nyckel'}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-textSecondary mb-1">{t('vehicleCard.userId')}</label>
                <input
                  type="text"
                  value={assignUserId}
                  onChange={e => setAssignUserId(e.target.value)}
                  placeholder={t('vehicleCard.userIdPlaceholder')}
                  className="w-full px-3 py-2 border border-border rounded bg-bgBase text-textDefault"
                />
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">{t('vehicleCard.validFrom')}</label>
                <DateTimeLocalInput value={assignValidFrom}
                  onChange={e => setAssignValidFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded bg-bgBase text-textDefault"
                />
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">{t('flinkey.validUntil') || 'Giltig till (valfritt)'}</label>
                <DateTimeLocalInput value={assignValidUntil}
                  onChange={e => setAssignValidUntil(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded bg-bgBase text-textDefault"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 border border-border rounded hover:bg-bgLight text-textDefault"
              >
                {t('common.cancel') || 'Avbryt'}
              </button>
              <button
                type="button"
                onClick={assignFlinkey}
                disabled={isAssigningFlinkey || !assignUserId.trim()}
                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
              >
                {isAssigningFlinkey ? (t('common.saving')) : (t('flinkey.assign') || 'Tilldela')}
              </button>
            </div>
          </div>
        </div>
      )}
      {mapOpen && vehicle?.id && (
        <VehiclePositionMapModal
          fallbackLabel={activeRegistrationNumber}
          onClose={() => setMapOpen(false)}
          load={loadVehicleMapPosition}
        />
      )}
    </div>
  );
};

export default React.memo(VehicleCard);
